import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../lib/utils';

export interface SignatureItem {
  id: number;
  name: string;
  body: string | null;
}

interface ManageSignaturesModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: { id: number; emailAddress: string } | null;
  signatures: SignatureItem[];
  colors?: Record<string, string>;
}

const ManageSignaturesModal: React.FC<ManageSignaturesModalProps> = ({
  isOpen,
  onClose,
  account,
  signatures,
  colors = {},
}) => {
  const { colors: themeColors } = useTheme();
  const { can } = useAuth();
  const canManage = can('email_signatures', 'view');
  const c = { ...themeColors, ...colors };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        className={cn(
          'relative flex flex-col rounded-xl shadow-2xl border overflow-hidden',
          c.surface,
          c.border
        )}
        style={{
          width: 'min(90vw, 420px)',
          maxHeight: '85vh',
          overflow: 'auto',
        }}
      >
        <div className={cn('flex items-center justify-between gap-4 px-4 py-3 border-b rounded-t-xl flex-shrink-0', c.border)}>
          <div className="min-w-0">
            <h2 className={cn('text-base font-semibold truncate', c.text)}>Signatures</h2>
            {account && (
              <p className={cn('text-xs truncate mt-0.5', c.textMuted)}>{account.emailAddress}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cn('p-1.5 rounded-lg transition-colors', c.textMuted, 'hover:bg-black/5 dark:hover:bg-white/5')}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-3">
          {signatures.length === 0 ? (
            <p className={cn('text-sm', c.textMuted)}>No signatures assigned to you yet.</p>
          ) : (
            <ul className="space-y-2">
              {signatures.map((sig) => (
                <li key={sig.id} className={cn('text-sm', c.text)}>
                  <span className="font-medium">{sig.name}</span>
                  {sig.body && (
                    <span className={cn('block text-xs mt-0.5 line-clamp-2', c.textMuted)}>
                      {sig.body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80)}
                      {sig.body.length > 80 ? '…' : ''}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <Link
            to="/email-signatures"
            onClick={onClose}
            className={cn(
              'inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium px-3 py-2 w-full transition-colors',
              c.primary,
              c.primaryText,
              'hover:opacity-90'
            )}
          >
            {canManage ? 'Manage all signatures' : 'Edit my signatures'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ManageSignaturesModal;
